/**
 * useBenchmarkLines (HOF-19 Phase B) — the retail observations behind ONE
 * benchmark cell, fetched only when a reader asks for them.
 *
 * `useBucketLines`' one rule, inherited whole: **on demand means on demand.**
 * `enabled` gates the query on the panel section actually being open, so a
 * rendered chart issues nothing and a drawn AMI line issues nothing. The server
 * holds the other half — `item`, `series_id` and `date` are all REQUIRED there,
 * so no request spells "every AMI row in the window".
 *
 * The key carries all four parameters because `(item, seriesId, date, bucket)`
 * IS the cell — and `bucket` matters here more than on the own-price panel: a
 * benchmark point at grain 1W is a whole week of shelf visits, so the same
 * `date` under `day` and `week` are two different questions.
 */
import { useQuery } from '@tanstack/react-query';
import { twinService, type PreisspiegelBenchmarkLinesResponse } from '../services/twinService';

export type BenchmarkLineBucket = 'day' | 'week' | 'month';

export const benchmarkLineKeys = {
  all: ['preisspiegel', 'benchmark-lines'] as const,
  cell: (item: string, seriesId: string, date: string, bucket: BenchmarkLineBucket) =>
    [...benchmarkLineKeys.all, item, seriesId, date, bucket] as const,
};

export interface BenchmarkLinesResult {
  data: PreisspiegelBenchmarkLinesResponse | undefined;
  isLoading: boolean;
  isError: boolean;
}

export function useBenchmarkLines(
  params: { item: string; seriesId: string; date: string; bucket: BenchmarkLineBucket },
  enabled: boolean,
): BenchmarkLinesResult {
  const query = useQuery({
    queryKey: benchmarkLineKeys.cell(params.item, params.seriesId, params.date, params.bucket),
    queryFn: () =>
      twinService.getPreisspiegelBenchmarkLines({
        item: params.item,
        seriesId: params.seriesId,
        date: params.date,
        bucket: params.bucket,
      }),
    enabled,
    // The panel is historical shelf data behind an import that runs by hand;
    // nothing about it changes on a page's timescale.
    staleTime: 300_000,
    gcTime: 1_800_000,
  });
  return { data: query.data, isLoading: query.isLoading, isError: query.isError };
}
